/**
 * 생성 `.py` 의 줄 가운데 **이름 길이에 따라 모양이 바뀌는 줄**을 `ruff format` 결과
 * 그대로 적는다.
 *
 * dp-lakehouse CI 는 `ruff check` 뿐 아니라 `ruff format --check` 도 돌린다. 고정된
 * 줄(따옴표·끝 쉼표·들여쓰기)은 템플릿에 ruff 모양으로 적으면 되지만, 아래 줄은
 * 88 자를 넘는 순간 ruff 가 다르게 접는다. 테이블 이름이 정하는 길이라 템플릿
 * 하나로는 맞출 수 없다.
 *
 * 규칙은 ruff 0.15.7(dp-lakehouse `pyproject.toml` 의 버전)을 테이블 이름 1~120 자로
 * 돌려 뽑았다. ruff 를 올리면 `pyFormat.test.ts` 의 기대값을 그 버전으로 다시
 * 확인해야 한다.
 */

/** dp-lakehouse `[tool.ruff] line-length`. */
export const PY_LINE_LENGTH = 88;

const fits = (line: string) => line.length <= PY_LINE_LENGTH;

/** `conf = tasks["key"]` — 넘치면 대괄호 안을 다음 줄로 내린다. */
export function pyTaskConfAssignment(conf: string, key: string): string {
  const flat = `${conf} = tasks["${key}"]`;
  return fits(flat) ? flat : `${conf} = tasks[\n    "${key}"\n]`;
}

/**
 * `    op = BigQueryInsertJobOperator(durable=False, **conf)`.
 *
 * ruff 가 고르는 순서: 한 줄 → (머리줄이 들어가면) 괄호 안을 한 줄로 → 인자마다
 * 한 줄 → (머리줄이 넘치면) 우변을 괄호로 감싸 그 안에서 같은 순서 → 감싸도
 * 넘치면 감싸지 않은 인자마다 한 줄로 되돌아간다.
 */
export function pyOperatorAssignment(op: string, conf: string): string {
  const call = 'BigQueryInsertJobOperator(';
  const args = `durable=False, **${conf}`;
  const flat = `    ${op} = ${call}${args})`;
  if (fits(flat)) return flat;

  const head = `    ${op} = ${call}`;
  const exploded = (pad: string) => [`${pad}durable=False,`, `${pad}**${conf},`];
  const unwrapped = (): string[] =>
    fits(`        ${args}`)
      ? [head, `        ${args}`, '    )']
      : [head, ...exploded('        '), '    )'];
  if (fits(head)) return unwrapped().join('\n');

  const inner = `        ${call}${args})`;
  if (fits(inner)) return [`    ${op} = (`, inner, '    )'].join('\n');
  if (fits(`            ${args}`)) {
    return [`    ${op} = (`, `        ${call}`, `            ${args}`, '        )', '    )'].join(
      '\n',
    );
  }
  if (fits(`            **${conf},`)) {
    return [
      `    ${op} = (`,
      `        ${call}`,
      ...exploded('            '),
      '        )',
      '    )',
    ].join('\n');
  }
  return unwrapped().join('\n');
}

/** `    a >> b >> c` — 넘치면 괄호로 감싸 연산자마다 한 줄. */
export function pyChain(operands: string[]): string {
  const flat = `    ${operands.join(' >> ')}`;
  if (fits(flat)) return flat;
  return [
    '    (',
    `        ${operands[0]}`,
    ...operands.slice(1).map(o => `        >> ${o}`),
    '    )',
  ].join('\n');
}

/**
 * `    "key": ["value"],` — 원소 하나짜리 리스트. 넘치면 원소를 다음 줄로 내리고,
 * 내린 줄이 들어갈 때만 끝 쉼표를 붙인다(넘치는 원소에는 ruff 가 붙이지 않는다).
 */
export function pySingletonListEntry(key: string, value: string): string {
  const flat = `    "${key}": ["${value}"],`;
  if (fits(flat)) return flat;
  const element = `        "${value}"`;
  return fits(`${element},`)
    ? [`    "${key}": [`, `${element},`, '    ],'].join('\n')
    : [`    "${key}": [`, element, '    ],'].join('\n');
}
